import React, { useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { GalleryImage } from '../data/site';
type LightboxProps = {
  images: GalleryImage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
export function Lightbox({
  images,
  index,
  onClose,
  onChange
}: LightboxProps) {
  const isOpen = index !== null;
  const goPrev = useCallback(() => {
    if (index === null) return;
    onChange((index - 1 + images.length) % images.length);
  }, [index, images.length, onChange]);
  const goNext = useCallback(() => {
    if (index === null) return;
    onChange((index + 1) % images.length);
  }, [index, images.length, onChange]);
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose, goPrev, goNext]);
  return <AnimatePresence data-id="element-180" data-mp="1qbr7fajhqluy">
      {isOpen && index !== null && <motion.div className="fixed inset-0 z-[70] flex items-center justify-center bg-ocean-deep/95 p-4 sm:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} role="dialog" aria-modal="true" aria-label="Image gallery" onClick={onClose} data-id="element-181" data-mp="2n0yrsf8dioxa">
          <button type="button" onClick={onClose} aria-label="Close gallery" className="absolute right-4 top-4 rounded-full border border-white/25 p-2.5 text-ivory transition-colors hover:bg-white/10 sm:right-8 sm:top-8" data-id="element-182" data-mp="12l4xtetu8tiu">
            <X className="h-5 w-5" data-id="element-183" data-mp="141r1xx2d11wz" />
          </button>

          <button type="button" onClick={event => {
        event.stopPropagation();
        goPrev();
      }} aria-label="Previous image" className="absolute left-3 z-10 rounded-full border border-white/25 p-2.5 text-ivory transition-colors hover:bg-white/10 sm:left-8" data-id="element-184" data-mp="1418zgip3trgm">
            <ChevronLeft className="h-5 w-5" data-id="element-185" data-mp="l2mz561ew4dr" />
          </button>

          <button type="button" onClick={event => {
        event.stopPropagation();
        goNext();
      }} aria-label="Next image" className="absolute right-3 z-10 rounded-full border border-white/25 p-2.5 text-ivory transition-colors hover:bg-white/10 sm:right-8" data-id="element-186" data-mp="l4zo435l7f0b">
            <ChevronRight className="h-5 w-5" data-id="element-187" data-mp="2copilhyu380" />
          </button>

          <motion.figure key={images[index].src} className="max-h-full w-full max-w-4xl" initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} onClick={event => event.stopPropagation()} data-id="element-188" data-mp="3evzu7e9coe17">
            <img src={images[index].src} alt={images[index].alt} className="mx-auto max-h-[78vh] w-auto rounded-sm object-contain" data-id="element-189" data-mp="3jub41oznaojf" />
            <figcaption className="mt-4 text-center text-xs uppercase tracking-wider-xl text-ivory/60" data-id="element-190" data-mp="3swkao3rslxrj">
              {index + 1} / {images.length} — {images[index].alt}
            </figcaption>
          </motion.figure>
        </motion.div>}
    </AnimatePresence>;
}